<template>
  <div class="contact wrapper">
    <LabeledTitle
      v-if="content.hero"
      :content="content.hero"
      :noanimation="true"
      class="contact__title"
    />

    <div class="contact__layout">
      <div class="contact__sidebar">
        <div class="contact__filter">
          <Dropdown
            v-if="currentPage"
            id="links"
            v-model="filter"
            :options="dropdownOptions"
            class="form-dropdown"
          >
            <template #trigger>
              <span class="form-label">{{
                chrome.language === 'en'
                  ? 'Select a subject'
                  : 'Sélectionner un sujet'
              }}</span>
              <div class="p-big form-dropdown__label">
                {{ currentPage.title }}
                <SvgSprite symbol="ui-arrow" size="15" />
              </div>
            </template>
          </Dropdown>

          <div class="contact__filter__content">
            <router-link
              v-for="link in content.links"
              :key="link.title"
              :to="link.url"
              :class="{
                'is-active': $route.path
                  .replaceAll('/', '')
                  .includes(link.url.replaceAll('/', '')),
              }"
              class="contact__filter__link"
            >
              {{ link.title }}
              <SvgSprite
                symbol="ui-full-arrow"
                class="contact__filter__link__icon"
                size="28"
              />
            </router-link>
          </div>
        </div>

        <div class="contact__address desktop">
          <TextLabel
            v-for="data in contactData"
            :key="data.label"
            :content="data"
            class="contact__address__item"
          />
        </div>
      </div>

      <div class="contact__form p-d">
        <component :is="forms[content.type]" :content="content" />
      </div>

      <div class="contact__address mobile p-d">
        <TextLabel
          v-for="data in contactData"
          :key="data.label"
          :content="data"
          class="contact__address__item"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, DefineComponent, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'

import HelloForm from '@/views/pages/HelloForm.vue'
import JobsForm from '@/views/pages/JobsForm.vue'
import LabeledTitle from '@/components/ui/LabeledTitle.vue'
import NewProject from '@/views/pages/NewProject.vue'
import TextLabel from '@/components/ui/TextLabel.vue'
import Dropdown from '@/components/ui/Dropdown.vue'
import { useChromeStore } from '@/stores/chrome'

const props = defineProps({
  content: {
    type: Object,
    required: true,
  },
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const forms: { [key: string]: DefineComponent<any, any, any> } = {
  hello: HelloForm,
  job: JobsForm,
  project: NewProject,
}

const chrome = useChromeStore()
const router = useRouter()
const route = useRoute()
const currentPage = computed(() =>
  props.content.links.find(l => l.url.includes(route.path))
)
const filter = ref()
const dropdownOptions = props.content.links
  .filter((l: Record<string, string>) => l.title !== currentPage.value?.title)
  .map((l: Record<string, string>) => ({ label: l.title, value: l.url }))
watch(filter, () => {
  router.push(filter.value)
})

const contactData = computed(() => [
  {
    label: chrome.t('address-label'),
    text: `<a href="${chrome.address.gmapsUrl}" target="_blank">
        ${props.content.address.name}<br />
        ${props.content.address.street} ${props.content.address.number}<br />
        ${props.content.address.zipcode} ${props.content.address.city}<br />
        ${props.content.address.country}
      </a>`,
  },
  {
    label: chrome.t('phone-label'),
    text: `<a href="${props.content.address.phone.value}">
        ${props.content.address.phone.label}
      </a>`,
  },
])
</script>

<style lang="scss" scoped>
$bp: xl;

.contact__title {
  margin-bottom: 0;

  :deep(.split-container) {
    margin-left: -1rem;
    padding-left: 1rem;
  }
}

.contact__layout {
  display: grid;
  grid-template: 'top' 'center' 'bottom';
  grid-template-rows: auto 1fr;
  grid-template-columns: 1fr;
  grid-gap: 0;

  @include mq($bp) {
    grid-template:
      'filter right'
      'contact right';
    grid-template-rows: auto 31rem 1fr;
    grid-template-columns: col(4) col(8);
    align-items: flex-start;
  }
}

.contact__sidebar {
  @include mq($bp) {
    position: sticky;
    z-index: 2;
    top: 8rem;
    display: block;
  }
}

.contact__filter {
  padding: 4rem 0;
  border-bottom: 1px solid var(--c-foreground);

  @include mq(s) {
    display: flex;
    padding: 4rem;
  }

  @include mq($bp) {
    display: block;
    grid-area: filter;
  }
}

.form-dropdown {
  :deep(.list) {
    @extend %list-nostyle;

    a {
      color: inherit;
      font-size: 20px;
    }
  }
}

.contact__filter__dropdown {
  display: block;

  :deep(.trigger) {
    width: 100%;
    padding: 1rem;
    color: var(--c-foreground);
    text-align: left;
    background-color: transparent;
    border: 1px solid var(--c-foreground);
    border-radius: 4px;
  }

  :deep(.trigger.is-expanded) {
    border-bottom: 0;
    border-radius: 4px 4px 0 0;
  }

  :deep(.list) {
    overflow: hidden;
    width: calc(100% - 1px);
    background: var(--c-background);
    border: 1px solid var(--c-foreground);
    border-radius: 0 0 4px 4px;
  }

  :deep(.option) {
    padding: 1.5rem;
    color: var(--c-foreground);
    font-size: 1.5rem;
    line-height: 1;
    letter-spacing: 0.5px;
    background: var(--c-background);

    &:hover {
      color: var(--c-background);
      background: var(--c-foreground);
    }
  }

  @include mq(s) {
    display: none;
  }
}

.contact__filter__content {
  display: none;

  @include mq(s) {
    display: flex;
    width: 100%;
  }

  @include mq($bp) {
    display: block;
  }
}

.contact__filter__link {
  @extend %fw-bold;

  display: flex;
  justify-content: center;
  align-items: center;
  flex-basis: 33.33%;
  color: var(--c-foreground);
  font-family: $ff-alt;
  font-size: 2.5rem;
  line-height: 1.6;
  text-align: center;
  text-transform: uppercase;
  white-space: nowrap;
  opacity: 0.5;
  transition: opacity 0.15s;

  &.is-active,
  &:hover {
    opacity: 1;
  }

  @include mq($bp) {
    justify-content: flex-start;
    margin-bottom: 1rem;
    text-align: left;
  }
}

.contact__filter__link__icon {
  flex-shrink: 0;
  margin-left: 2.5rem;
  fill: var(--c-foreground);
  opacity: 0;
  transform: translateX(-15px);
  transition: 0.5s $ease-out-expo;

  .contact__filter__link.is-active &,
  .contact__filter__link:hover & {
    opacity: 1;
    transform: translateX(0);
  }

  @include mq($until: $bp) {
    display: none;
  }
}

.contact__address {
  display: none;
  border-top: 1px solid var(--c-foreground);

  &.desktop {
    display: none;
  }

  // @include mq(s) {
  //   display: flex;
  //   justify-content: space-between;
  // }

  @include mq($bp) {
    display: block;

    &.desktop {
      display: block;
      padding: 4rem;
      border-top: 0;
    }

    &.mobile {
      display: none;
    }
  }
}

.contact__address__item {
  &:not(:last-child) {
    margin-bottom: 4rem;
  }

  @include mq(s, xl) {
    &:not(:last-child) {
      margin-bottom: 0;
    }
  }
}

.contact__form {
  grid-area: center;

  @include mq(s) {
    padding-right: col(1, 8);
    padding-left: col(1, 8);
  }

  @include mq($bp) {
    grid-area: right;
    border-left: 1px solid var(--c-foreground);
  }
}
</style>
